import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { scrollTo } from "@/components/Navbar";

const HERO_IMG = "/la-freeway-night.webp";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Los Angeles 110 freeway and downtown skyline at night"
          className="h-full w-full object-cover object-[center_35%] opacity-75"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,11,14,0.28)_0%,rgba(10,11,14,0.58)_76%,#0A0B0E_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(10,11,14,0.85)_0%,rgba(10,11,14,0.36)_52%,rgba(10,11,14,0.08)_100%)]" />
      <div className="grid-lines absolute inset-0 opacity-40" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10"
      >
        <div className="max-w-3xl">
          <div>
            <motion.div
              data-testid="hero-live-badge"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-3 border border-[#222736] bg-[#12141C]/70 px-4 py-2 backdrop-blur"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10B981] opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#10B981]" />
              </span>
              <span className="font-mono text-[10px] font-semibold tracking-[0.2em] text-slate-300 sm:text-[11px]">
                SHIPPER + CARRIER PARTNERSHIPS
              </span>
            </motion.div>

            <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
              <Line delay={0.35}>Freight moved</Line>
              <Line delay={0.5}>
                with <span className="text-[#FF5500]">surgical</span>
              </Line>
              <Line delay={0.65}>precision.</Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-7 text-slate-300 sm:text-lg sm:leading-8"
            >
              Merchant Alliance Brokerage supports shippers and carriers with
              direct freight coordination, equipment matching, clear
              communication, and support from pickup through delivery.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-rate-cta"
                onClick={() => scrollTo("#pathways")}
                className="group inline-flex items-center gap-2 bg-[#FF5500] px-7 py-4 font-mono text-xs font-bold tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#F59E0B]"
              >
                REQUEST A FREIGHT QUOTE
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </motion.div>
          </div>

        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-button"
        onClick={() => scrollTo("#pathways")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500 transition-colors hover:text-[#FF5500]"
        aria-label="Explore carrier and shipper pathways"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block"
        >
          <ChevronDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
}
